import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

import { useProductReadContext } from '@app/context/ProductContext'

import { TourDefinition } from '@ui/components/Tourtip/Tourtip'

import { getTourState } from './store'
import { useTourRoute } from './useTourRoute'

const isEligible = (def: TourDefinition): boolean => {
  const tourState = getTourState(def.id)
  return tourState !== 'completed' && tourState !== 'dismissed'
}

const waitForIdle = (ms: number): Promise<boolean> => {
  return new Promise((resolve) => {
    let done = false

    const finish = (idle: boolean) => {
      if (done) return
      done = true
      cleanup()
      resolve(idle)
    }

    const cancelIdle = () => finish(false)

    const listeners: Array<[string, EventListenerOrEventListenerObject, AddEventListenerOptions?]> =
      [
        ['pointerdown', cancelIdle, { passive: true }],
        ['click', cancelIdle, { passive: true }],
        ['touchstart', cancelIdle, { passive: true }],
        [
          'keydown',
          (e: Event) => {
            const ke = e as KeyboardEvent
            if (ke.key === 'Enter' || ke.key === ' ' || ke.key === 'Spacebar') {
              cancelIdle()
            }
          },
        ],
        ['menu:open', cancelIdle],
        ['chat:open', cancelIdle],
      ]

    listeners.forEach(([type, handler, options]) => {
      window.addEventListener(type, handler, options)
    })

    const timer = setTimeout(() => finish(true), ms)

    const cleanup = () => {
      clearTimeout(timer)
      listeners.forEach(([type, handler]) => {
        window.removeEventListener(type, handler)
      })
    }
  })
}

const IDLE_TIMEOUT_MS = 1000

const startTour = (def: TourDefinition) => {
  window.dispatchEvent(new CustomEvent('tour:start', { detail: def }))
}

export const useTourOrchestration = () => {
  const { pathname } = useLocation()
  const tour = useTourRoute(pathname)
  const { product } = useProductReadContext()

  useEffect(() => {
    if (!tour || !isEligible(tour) || !tour.products.find((p) => p === product)) {
      return
    }

    let cancelled = false

    const runTour = async () => {
      const isIdle = await waitForIdle(IDLE_TIMEOUT_MS)
      if (cancelled || !isIdle) return
      startTour(tour)
    }

    runTour()

    return () => {
      cancelled = true
    }
  }, [tour, product])
}
